import { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Check, Star, Crown, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { useFounderOffer } from '@/hooks/useFounderOffer';
import { useAnalytics } from '@/hooks/useAnalytics';
import { PricingComparisonTable } from './PricingComparisonTable';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/i18n/LanguageContext';
import { lc } from '@/i18n/copy';

const copy = {
  en: {
    freeBanner: 'Not ready to upgrade? You can start with <b>3 free announcements</b> — no payment required.',
    freeBannerCta: 'Start Free →',
    heading: 'Choose how you want to show up.',
    subheading: 'Start free, or choose a plan that helps you stay present more often.',
    oneTime: 'one-time',
    starter: 'Starter',
    starterDesc: 'For saying what matters — once.',
    starterCta: 'Try It Once',
    starterFeatures: ['+5 Announcements', 'Basic AI Writing Assist', 'Remove watermark'],
    connection: 'Connection',
    connectionDesc: 'For staying present — and saying what matters more often.',
    connectionTagline: "For people who don't want to wait until it's too late.",
    connectionCta: 'Stay Connected',
    connectionFeatures: ['+10 Announcements', 'Full AI Writing Assist', 'Schedule Messages for Later', 'Print & PDF Export', 'Magazine Flipbook View', 'Everything in Starter'],
    mostPopular: 'Most Popular',
    founderPhase1: 'Founder Phase 1 — {spots} spots left at ${price}',
    founderPhase1Sub: 'Lock in the lowest lifetime rate.',
    founderPhase2: 'Founder Phase 2 — {spots} spots remaining at ${price}',
    founderPhase2Sub: 'Founder pricing ends when this fills.',
    legacy: 'Legacy',
    legacyTag: 'For the Long View',
    legacyDesc: 'For reflection, continuity, and messages that outlive the moment.',
    legacyTagline: 'This is for people building something that lasts.',
    legacyCta: 'Build Your Legacy',
    legacyFeatures: ['+25 Announcements', 'Video Messaging', 'Talk-to-Text', 'TRESLOG', 'Recurring Annual Announcements', 'Downloadable Keepsakes', 'Everything in Connection'],
    legacyFooter: 'For the long view. Not just messages — memory.',
    bottomNote: 'Your words are saved. You can return to them anytime.',
    bottomSub: 'No hidden fees. No subscription required. One-time purchase. Credits never expire.',
  },
  es: {
    freeBanner: '¿No estás listo para actualizar? Puedes comenzar con <b>3 anuncios gratis</b> — sin pago requerido.',
    freeBannerCta: 'Comenzar Gratis →',
    heading: 'Elige Cómo Quieres Presentarte',
    subheading: '¿Dirás algo una vez — o construirás algo duradero?',
    oneTime: 'único pago',
    starter: 'Inicio',
    starterDesc: 'Para decir lo que importa — una vez.',
    starterCta: 'Pruébalo Una Vez',
    starterFeatures: ['+5 Anuncios', 'Escritura IA Básica', 'Eliminar marca de agua'],
    connection: 'Conexión',
    connectionDesc: 'Para estar presente — y decir lo que importa más seguido.',
    connectionTagline: 'Para personas que no quieren esperar hasta que sea demasiado tarde.',
    connectionCta: 'Mantente Conectado',
    connectionFeatures: ['+10 Anuncios', 'Escritura IA Completa', 'Programar Mensajes', 'Exportar PDF', 'Vista Revista', 'Todo en Inicio'],
    mostPopular: 'Más Popular',
    founderPhase1: 'Fase Fundador 1 — {spots} lugares a ${price}',
    founderPhase1Sub: 'Asegura la tarifa más baja de por vida.',
    founderPhase2: 'Fase Fundador 2 — {spots} lugares restantes a ${price}',
    founderPhase2Sub: 'El precio fundador termina al llenarse.',
    legacy: 'Legado',
    legacyTag: 'Para la Vista a Largo Plazo',
    legacyDesc: 'Para reflexión, continuidad, y mensajes que trascienden el momento.',
    legacyTagline: 'Para personas que construyen algo duradero.',
    legacyCta: 'Construye Tu Legado',
    legacyFeatures: ['+25 Anuncios', 'Mensajes en Video', 'Voz a Texto', 'TRESLOG', 'Anuncios Anuales Recurrentes', 'Recuerdos Descargables', 'Todo en Conexión'],
    legacyFooter: 'Para la vista a largo plazo. No solo mensajes — memoria.',
    bottomNote: 'Tus palabras se guardan. Puedes volver a ellas en cualquier momento.',
    bottomSub: 'Sin cargos ocultos. Sin suscripción. Compra única. Los créditos nunca expiran.',
  },
  pt: {
    freeBanner: 'Não está pronto para atualizar? Comece com <b>3 anúncios grátis</b> — sem pagamento.',
    freeBannerCta: 'Começar Grátis →',
    heading: 'Escolha Como Quer Se Apresentar',
    subheading: 'Vai dizer algo uma vez — ou construir algo duradouro?',
    oneTime: 'pagamento único',
    starter: 'Inicial',
    starterDesc: 'Para dizer o que importa — uma vez.',
    starterCta: 'Experimente Uma Vez',
    starterFeatures: ['+5 Anúncios', 'Escrita IA Básica', 'Remover marca d\'água'],
    connection: 'Conexão',
    connectionDesc: 'Para estar presente — e dizer o que importa com mais frequência.',
    connectionTagline: 'Para pessoas que não querem esperar até que seja tarde demais.',
    connectionCta: 'Fique Conectado',
    connectionFeatures: ['+10 Anúncios', 'Escrita IA Completa', 'Agendar Mensagens', 'Exportar PDF', 'Visualização Revista', 'Tudo no Inicial'],
    mostPopular: 'Mais Popular',
    founderPhase1: 'Fase Fundador 1 — {spots} vagas a ${price}',
    founderPhase1Sub: 'Garanta a menor tarifa vitalícia.',
    founderPhase2: 'Fase Fundador 2 — {spots} vagas restantes a ${price}',
    founderPhase2Sub: 'Preço fundador termina ao encher.',
    legacy: 'Legado',
    legacyTag: 'Para a Visão de Longo Prazo',
    legacyDesc: 'Para reflexão, continuidade, e mensagens que transcendem o momento.',
    legacyTagline: 'Para pessoas construindo algo que dura.',
    legacyCta: 'Construa Seu Legado',
    legacyFeatures: ['+25 Anúncios', 'Mensagens em Vídeo', 'Voz para Texto', 'TRESLOG', 'Anúncios Anuais Recorrentes', 'Lembranças para Download', 'Tudo no Conexão'],
    legacyFooter: 'Para a visão de longo prazo. Não apenas mensagens — memória.',
    bottomNote: 'Suas palavras são salvas. Você pode voltar a elas a qualquer momento.',
    bottomSub: 'Sem taxas ocultas. Sem assinatura. Compra única. Créditos nunca expiram.',
  },
  fr: {
    freeBanner: 'Pas prêt à upgrader ? Commencez avec <b>3 annonces gratuites</b> — sans paiement.',
    freeBannerCta: 'Commencer Gratuitement →',
    heading: 'Choisissez Comment Vous Voulez Vous Présenter',
    subheading: 'Dites-vous quelque chose une fois — ou construisez-vous quelque chose de durable ?',
    oneTime: 'paiement unique',
    starter: 'Débutant',
    starterDesc: 'Pour dire ce qui compte — une fois.',
    starterCta: 'Essayer Une Fois',
    starterFeatures: ['+5 Annonces', 'Aide IA Basique', 'Supprimer filigrane'],
    connection: 'Connexion',
    connectionDesc: 'Pour rester présent — et dire ce qui compte plus souvent.',
    connectionTagline: 'Pour ceux qui ne veulent pas attendre qu\'il soit trop tard.',
    connectionCta: 'Restez Connecté',
    connectionFeatures: ['+10 Annonces', 'Aide IA Complète', 'Programmer des Messages', 'Export PDF', 'Vue Magazine', 'Tout dans Débutant'],
    mostPopular: 'Le Plus Populaire',
    founderPhase1: 'Phase Fondateur 1 — {spots} places à {price}$',
    founderPhase1Sub: 'Verrouillez le tarif le plus bas à vie.',
    founderPhase2: 'Phase Fondateur 2 — {spots} places restantes à {price}$',
    founderPhase2Sub: 'Le prix fondateur se termine au remplissage.',
    legacy: 'Héritage',
    legacyTag: 'Pour la Vision à Long Terme',
    legacyDesc: 'Pour la réflexion, la continuité, et des messages qui survivent au moment.',
    legacyTagline: 'Pour ceux qui construisent quelque chose qui dure.',
    legacyCta: 'Construisez Votre Héritage',
    legacyFeatures: ['+25 Annonces', 'Messages Vidéo', 'Voix en Texte', 'TRESLOG', 'Annonces Annuelles Récurrentes', 'Souvenirs Téléchargeables', 'Tout dans Connexion'],
    legacyFooter: 'Pour la vision à long terme. Pas juste des messages — de la mémoire.',
    bottomNote: 'Vos mots sont sauvegardés. Vous pouvez y revenir à tout moment.',
    bottomSub: 'Pas de frais cachés. Pas d\'abonnement. Achat unique. Les crédits n\'expirent jamais.',
  },
  ar: {
    freeBanner: 'لست مستعداً للترقية؟ يمكنك البدء بـ <b>3 إعلانات مجانية</b> — بدون دفع.',
    freeBannerCta: 'ابدأ مجاناً ←',
    heading: 'اختر كيف تريد أن تظهر',
    subheading: 'هل تقول شيئاً مرة واحدة — أم تبني شيئاً دائماً؟',
    oneTime: 'دفعة واحدة',
    starter: 'البداية',
    starterDesc: 'لقول ما يهم — مرة واحدة.',
    starterCta: 'جربها مرة',
    starterFeatures: ['+5 إعلانات', 'مساعدة ذكاء اصطناعي أساسية', 'إزالة العلامة المائية'],
    connection: 'الاتصال',
    connectionDesc: 'للبقاء حاضراً — وقول ما يهم أكثر.',
    connectionTagline: 'لمن لا يريدون الانتظار حتى فوات الأوان.',
    connectionCta: 'ابقَ متصلاً',
    connectionFeatures: ['+10 إعلانات', 'مساعدة ذكاء اصطناعي كاملة', 'جدولة الرسائل', 'تصدير PDF', 'عرض المجلة', 'كل شيء في البداية'],
    mostPopular: 'الأكثر شعبية',
    founderPhase1: 'مرحلة المؤسس 1 — {spots} مكان بسعر ${price}',
    founderPhase1Sub: 'احجز أقل سعر مدى الحياة.',
    founderPhase2: 'مرحلة المؤسس 2 — {spots} مكان متبقي بسعر ${price}',
    founderPhase2Sub: 'سعر المؤسس ينتهي عند الامتلاء.',
    legacy: 'الإرث',
    legacyTag: 'للرؤية طويلة المدى',
    legacyDesc: 'للتأمل والاستمرارية ورسائل تتجاوز اللحظة.',
    legacyTagline: 'لمن يبنون شيئاً يدوم.',
    legacyCta: 'ابنِ إرثك',
    legacyFeatures: ['+25 إعلان', 'رسائل فيديو', 'تحويل الصوت لنص', 'TRESLOG', 'إعلانات سنوية متكررة', 'تذكارات قابلة للتحميل', 'كل شيء في الاتصال'],
    legacyFooter: 'للرؤية طويلة المدى. ليست مجرد رسائل — ذاكرة.',
    bottomNote: 'كلماتك محفوظة. يمكنك العودة إليها في أي وقت.',
    bottomSub: 'بدون رسوم مخفية. بدون اشتراك. شراء لمرة واحدة. الأرصدة لا تنتهي.',
  },
  ja: {
    freeBanner: 'アップグレードの準備ができていませんか？<b>3つの無料アナウンス</b>から始められます — 支払い不要。',
    freeBannerCta: '無料で始める →',
    heading: 'どのように参加したいか選びましょう',
    subheading: '一度だけ伝えますか — それとも、何か永続的なものを作りますか？',
    oneTime: '一回払い',
    starter: 'スターター',
    starterDesc: '大切なことを伝える — 一度だけ。',
    starterCta: '一度試す',
    starterFeatures: ['+5 アナウンスメント', '基本AI文章支援', 'ウォーターマーク削除'],
    connection: 'コネクション',
    connectionDesc: '存在し続ける — そしてもっと頻繁に大切なことを伝える。',
    connectionTagline: '手遅れになるまで待ちたくない人のために。',
    connectionCta: 'つながり続ける',
    connectionFeatures: ['+10 アナウンスメント', '完全AI文章支援', 'メッセージ予約', 'PDF出力', 'マガジンビュー', 'スターターの全機能'],
    mostPopular: '一番人気',
    founderPhase1: 'ファウンダーフェーズ1 — 残り{spots}枠 ${price}',
    founderPhase1Sub: '最も低い生涯料金を確保。',
    founderPhase2: 'ファウンダーフェーズ2 — 残り{spots}枠 ${price}',
    founderPhase2Sub: '満員でファウンダー価格終了。',
    legacy: 'レガシー',
    legacyTag: '長期的な視野のために',
    legacyDesc: '振り返り、継続性、そして瞬間を超えるメッセージのために。',
    legacyTagline: '永続するものを作る人のために。',
    legacyCta: 'あなたのレガシーを作る',
    legacyFeatures: ['+25 アナウンスメント', 'ビデオメッセージ', '音声テキスト変換', 'TRESLOG', '年次定期アナウンス', 'ダウンロード可能な記念品', 'コネクションの全機能'],
    legacyFooter: '長期的な視野のために。メッセージだけではなく — 記憶。',
    bottomNote: 'あなたの言葉は保存されます。いつでも戻れます。',
    bottomSub: '隠れた費用なし。サブスクリプション不要。一回購入。クレジットは期限切れなし。',
  },
  ko: {
    freeBanner: '업그레이드 준비가 안 되셨나요? <b>무료 공지 3개</b>로 시작하세요 — 결제 불필요.',
    freeBannerCta: '무료로 시작 →',
    heading: '어떻게 나타나고 싶은지 선택하세요',
    subheading: '한 번만 말할 건가요 — 아니면 지속되는 것을 만들 건가요?',
    oneTime: '일회성',
    starter: '스타터',
    starterDesc: '중요한 것을 말하기 위해 — 한 번.',
    starterCta: '한 번 시도',
    starterFeatures: ['+5 공지', '기본 AI 작문 도움', '워터마크 제거'],
    connection: '커넥션',
    connectionDesc: '존재하기 위해 — 그리고 더 자주 중요한 것을 말하기 위해.',
    connectionTagline: '너무 늦을 때까지 기다리고 싶지 않은 사람을 위해.',
    connectionCta: '연결 유지',
    connectionFeatures: ['+10 공지', '전체 AI 작문 도움', '메시지 예약', 'PDF 내보내기', '매거진 뷰', '스타터의 모든 기능'],
    mostPopular: '가장 인기',
    founderPhase1: '파운더 1단계 — {spots}자리 남음 ${price}',
    founderPhase1Sub: '최저 평생 요금을 확보하세요.',
    founderPhase2: '파운더 2단계 — {spots}자리 남음 ${price}',
    founderPhase2Sub: '다 차면 파운더 가격이 종료됩니다.',
    legacy: '레거시',
    legacyTag: '장기적 관점을 위해',
    legacyDesc: '성찰, 연속성, 그리고 순간을 넘어서는 메시지를 위해.',
    legacyTagline: '지속되는 것을 만드는 사람을 위해.',
    legacyCta: '레거시 만들기',
    legacyFeatures: ['+25 공지', '비디오 메시지', '음성 텍스트 변환', 'TRESLOG', '연간 반복 공지', '다운로드 가능한 기념품', '커넥션의 모든 기능'],
    legacyFooter: '장기적 관점을 위해. 메시지뿐만 아니라 — 기억.',
    bottomNote: '당신의 말은 저장됩니다. 언제든 돌아올 수 있습니다.',
    bottomSub: '숨겨진 수수료 없음. 구독 불필요. 일회성 구매. 크레딧은 만료되지 않습니다.',
  },
  id: {
    freeBanner: 'Belum siap upgrade? Mulai dengan <b>3 pengumuman gratis</b> — tanpa pembayaran.',
    freeBannerCta: 'Mulai Gratis →',
    heading: 'Pilih Cara Anda Ingin Tampil',
    subheading: 'Apakah Anda mengatakan sesuatu sekali — atau membangun sesuatu yang bertahan?',
    oneTime: 'sekali bayar',
    starter: 'Pemula',
    starterDesc: 'Untuk mengatakan yang penting — sekali.',
    starterCta: 'Coba Sekali',
    starterFeatures: ['+5 Pengumuman', 'Bantuan AI Dasar', 'Hapus watermark'],
    connection: 'Koneksi',
    connectionDesc: 'Untuk tetap hadir — dan mengatakan yang penting lebih sering.',
    connectionTagline: 'Untuk orang yang tidak ingin menunggu sampai terlambat.',
    connectionCta: 'Tetap Terhubung',
    connectionFeatures: ['+10 Pengumuman', 'Bantuan AI Lengkap', 'Jadwalkan Pesan', 'Ekspor PDF', 'Tampilan Majalah', 'Semua di Pemula'],
    mostPopular: 'Paling Populer',
    founderPhase1: 'Fase Pendiri 1 — {spots} tempat tersisa di ${price}',
    founderPhase1Sub: 'Kunci tarif terendah seumur hidup.',
    founderPhase2: 'Fase Pendiri 2 — {spots} tempat tersisa di ${price}',
    founderPhase2Sub: 'Harga pendiri berakhir saat penuh.',
    legacy: 'Warisan',
    legacyTag: 'Untuk Pandangan Jangka Panjang',
    legacyDesc: 'Untuk refleksi, kontinuitas, dan pesan yang melampaui momen.',
    legacyTagline: 'Untuk orang yang membangun sesuatu yang bertahan.',
    legacyCta: 'Bangun Warisan Anda',
    legacyFeatures: ['+25 Pengumuman', 'Pesan Video', 'Suara ke Teks', 'TRESLOG', 'Pengumuman Tahunan Berulang', 'Kenang-kenangan yang Bisa Diunduh', 'Semua di Koneksi'],
    legacyFooter: 'Untuk pandangan jangka panjang. Bukan hanya pesan — kenangan.',
    bottomNote: 'Kata-kata Anda tersimpan. Anda bisa kembali kapan saja.',
    bottomSub: 'Tanpa biaya tersembunyi. Tanpa langganan. Pembelian sekali. Kredit tidak pernah kedaluwarsa.',
  },

  hi: {
    freeBanner: 'अपग्रेड करने के लिए तैयार नहीं हैं? आप <b>3 निःशुल्क घोषणाओं</b> के साथ शुरू कर सकते हैं — कोई भुगतान आवश्यक नहीं है।',
    freeBannerCta: 'मुफ्त में शुरू करें →',
    heading: 'चुनें कि आप कैसे दिखना चाहते हैं।',
    subheading: 'मुफ्त में शुरू करें, या एक ऐसी योजना चुनें जो आपको अधिक बार उपस्थित रहने में मदद करती है।',
    oneTime: 'एक बार',
    starter: 'स्टार्टर',
    starterDesc: 'जो मायने रखता है उसे कहने के लिए — एक बार।',
    starterCta: 'एक बार आज़माएँ',
    starterFeatures: ['+5 Announcements', 'Basic AI Writing Assist', 'Remove watermark'],
    connection: 'कनेक्शन',
    connectionDesc: 'उपस्थित रहने के लिए — और जो मायने रखता है उसे अधिक बार कहने के लिए।',
    connectionTagline: "For people who don't want to wait until it's too late.",
    connectionCta: 'जुड़े रहें',
    connectionFeatures: ['+10 Announcements', 'Full AI Writing Assist', 'Schedule Messages for Later', 'Print & PDF Export', 'Magazine Flipbook View', 'Everything in Starter'],
    mostPopular: 'सबसे लोकप्रिय',
    founderPhase1: 'संस्थापक चरण 1 — ${price} पर {spots} स्थान शेष',
    founderPhase1Sub: 'सबसे कम आजीवन दर लॉक करें।',
    founderPhase2: 'संस्थापक चरण 2 — ${price} पर {spots} स्थान शेष',
    founderPhase2Sub: 'संस्थापक मूल्य निर्धारण तब समाप्त होता है जब यह भर जाता है।',
    legacy: 'विरासत',
    legacyTag: 'दीर्घकालिक दृष्टिकोण के लिए',
    legacyDesc: 'चिंतन, निरंतरता और उन संदेशों के लिए जो पल से आगे निकल जाते हैं।',
    legacyTagline: 'यह उन लोगों के लिए है जो कुछ स्थायी बना रहे हैं।',
    legacyCta: 'अपनी विरासत बनाएँ',
    legacyFeatures: ['+25 Announcements', 'Video Messaging', 'Talk-to-Text', 'TRESLOG', 'Recurring Annual Announcements', 'Downloadable Keepsakes', 'Everything in Connection'],
    legacyFooter: 'दीर्घकालिक दृष्टिकोण के लिए। केवल संदेश नहीं — स्मृति।',
    bottomNote: 'आपके शब्द सहेजे गए हैं। आप कभी भी उन पर वापस आ सकते हैं।',
    bottomSub: 'कोई छिपी हुई फीस नहीं। कोई सदस्यता आवश्यक नहीं। एक बार की खरीद। क्रेडिट कभी समाप्त नहीं होते।',
  },
};

export function PricingSectionV2() {
  const founder = useFounderOffer();
  const { trackEvent } = useAnalytics();
  const navigate = useNavigate();
  const trackedLoad = useRef(false);
  const prevPhase = useRef(founder.phase);
  const { language } = useLanguage();
  const t = lc(copy, language);

  useEffect(() => {
    if (!trackedLoad.current) {
      trackEvent('pricing_section_loaded');
      trackedLoad.current = true;
    }
  }, [trackEvent]);

  useEffect(() => {
    if (!founder.isLoading && prevPhase.current !== founder.phase) {
      trackEvent('founder_phase_changed', { phase: founder.phase, price: founder.price });
      prevPhase.current = founder.phase;
    }
  }, [founder.isLoading, founder.phase, founder.price, trackEvent]);

  const handleCTA = (tier: 'starter' | 'connection' | 'legacy' | 'free') => {
    trackEvent(`${tier}_cta_clicked`, { phase: founder.phase, price: founder.price });
    if (tier === 'free') trackEvent('free_signup_started');
    navigate('/signup');
  };

  const founderLabel = founder.phase === 'phase1'
    ? t.founderPhase1.replace('{spots}', String(founder.spotsLeft)).replace('{price}', String(founder.price))
    : t.founderPhase2.replace('{spots}', String(founder.spotsLeft)).replace('{price}', String(founder.price));
  const founderSub = founder.phase === 'phase1' ? t.founderPhase1Sub : t.founderPhase2Sub;

  return (
    <section id="pricing" className="py-20 px-4">
      <div className="container max-w-6xl mx-auto">
        {/* free tier banner */}
        <motion.div
          className="rounded-xl bg-emerald-500/10 border border-emerald-500/20 px-5 py-3 text-center mb-8 cursor-pointer hover:bg-emerald-500/15 transition-colors"
          initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          onClick={() => handleCTA('free')}
          role="button" tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter') handleCTA('free'); }}
        >
          <p className="text-sm text-emerald-700 dark:text-emerald-400" dangerouslySetInnerHTML={{
            __html: `${t.freeBanner} <span class="underline font-semibold">${t.freeBannerCta}</span>`
          }} />
        </motion.div>

        {/* header */}
        <motion.div className="text-center mb-14" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
          <h2 className="text-3xl md:text-4xl font-bold billboard-flash mb-3">{t.heading}</h2>
          <p className="text-lg text-muted-foreground max-w-xl mx-auto">{t.subheading}</p>
        </motion.div>

        {/* cards grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
          {/* STARTER */}
          <motion.div className="opacity-[0.95]" initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 0.95, y: 0 }} viewport={{ once: true }}>
            <Card className="h-full border-border bg-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-xl text-foreground">{t.starter}</CardTitle>
                <p className="text-sm text-muted-foreground">{t.starterDesc}</p>
              </CardHeader>
              <CardContent className="space-y-5">
                <div>
                  <span className="text-4xl font-bold text-foreground">$9</span>
                  <span className="text-sm text-muted-foreground ml-1">{t.oneTime}</span>
                </div>
                <ul className="space-y-2.5">
                  {t.starterFeatures.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-muted-foreground">
                      <Check className="h-4 w-4 mt-0.5 text-primary shrink-0" />{f}
                    </li>
                  ))}
                </ul>
                <Button variant="outline" className="w-full" onClick={() => handleCTA('starter')}>{t.starterCta}</Button>
              </CardContent>
            </Card>
          </motion.div>

          {/* CONNECTION */}
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }}>
            <Card className="h-full border-primary/40 bg-card shadow-lg shadow-primary/5 ring-1 ring-primary/20">
              <CardHeader className="pb-2">
                <div className="flex items-center gap-2 mb-1">
                  <CardTitle className="text-xl text-foreground">{t.connection}</CardTitle>
                  <Badge className="bg-primary text-primary-foreground text-xs">
                    <Star className="h-3 w-3 mr-1" /> {t.mostPopular}
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground">{t.connectionDesc}</p>
              </CardHeader>
              <CardContent className="space-y-5">
                <div>
                  <span className="text-4xl font-bold text-foreground">${founder.isLoading ? '—' : founder.price}</span>
                  <span className="text-sm text-muted-foreground ml-1">{t.oneTime}</span>
                </div>
                <p className="text-xs text-muted-foreground italic -mt-2">{t.connectionTagline}</p>
                {!founder.isLoading && founder.phase !== 'regular' && (
                  <div className="rounded-lg bg-primary/10 border border-primary/20 p-3 text-center space-y-1">
                    <p className="text-sm font-semibold text-primary">{founderLabel}</p>
                    <p className="text-xs text-muted-foreground">{founderSub}</p>
                  </div>
                )}
                <ul className="space-y-2.5">
                  {t.connectionFeatures.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-muted-foreground">
                      <Check className="h-4 w-4 mt-0.5 text-primary shrink-0" />{f}
                    </li>
                  ))}
                </ul>
                <Button className="w-full" onClick={() => handleCTA('connection')}>{t.connectionCta}</Button>
              </CardContent>
            </Card>
          </motion.div>

          {/* LEGACY */}
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.2 }}>
            <Card className="h-full border-amber-500/40 bg-card shadow-lg ring-1 ring-amber-500/20" style={{ minHeight: '110%' }}>
              <CardHeader className="pb-2">
                <div className="flex items-center gap-2 mb-1">
                  <CardTitle className="text-xl text-foreground">{t.legacy}</CardTitle>
                  <Badge variant="outline" className="border-amber-500/50 text-amber-600 dark:text-amber-400 text-xs">
                    <Crown className="h-3 w-3 mr-1" /> {t.legacyTag}
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground">{t.legacyDesc}</p>
                <p className="text-xs text-muted-foreground italic mt-1">{t.legacyTagline}</p>
              </CardHeader>
              <CardContent className="space-y-5">
                <div>
                  <span className="text-4xl font-bold text-foreground">$35</span>
                  <span className="text-sm text-muted-foreground ml-1">{t.oneTime}</span>
                </div>
                <ul className="space-y-2.5">
                  {t.legacyFeatures.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-muted-foreground">
                      <Check className="h-4 w-4 mt-0.5 text-amber-500 shrink-0" />{f}
                    </li>
                  ))}
                </ul>
                <Separator className="bg-amber-500/20" />
                <p className="text-xs text-center text-muted-foreground italic">{t.legacyFooter}</p>
                <Button className="w-full bg-amber-600 hover:bg-amber-700 text-white" onClick={() => handleCTA('legacy')}>
                  <Sparkles className="h-4 w-4 mr-1" />{t.legacyCta}
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        <PricingComparisonTable />

        <motion.div className="text-center mt-12 space-y-2" initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}>
          <p className="text-sm text-muted-foreground">{t.bottomNote}</p>
          <p className="text-xs text-muted-foreground/70">{t.bottomSub}</p>
        </motion.div>
      </div>
    </section>
  );
}
